/**
 * Batch upload (PROD-3204 DI-03): `POST api/Patients/{patientId}/Intake/Batches`, multipart.
 *
 * Sent with XMLHttpRequest, because `fetch` cannot report upload progress and a batch can be 100 files of 10 MB. The
 * headers come from the shared client (`authHeaders`), so auth and the context headers match every other call; a 401
 * is retried once with a refreshed token, as `api-client` does.
 *
 * The server is the gate for type and size: it refuses files one by one (`refusedCode`) and takes the rest.
 */
import { apiUrl, authHeaders } from '@/shared/api/api-client';
import type { IntakeUploadResponse } from '../types';
import { IntakeApiError, intakeBase, toIntakeError } from './intake-client';

export interface UploadBatchOptions {
  /** Called with 0–1 as the body goes up. */
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
  /** `{ labRequestIds }` when the upload starts from an order. */
  context?: { labRequestIds: string[] };
  /** The batch's `source` part. Default `upload`; `whatsapp` for a file sent from the WhatsApp inbox (PROD-3335). */
  source?: 'upload' | 'whatsapp';
}

function send(url: string, body: FormData, headers: Headers, opts: UploadBatchOptions) {
  return new Promise<{ status: number; text: string }>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    headers.forEach((value, key) => xhr.setRequestHeader(key, value));
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) opts.onProgress?.(e.loaded / e.total);
    };
    xhr.onload = () => resolve({ status: xhr.status, text: xhr.responseText });
    xhr.onerror = () => reject(new TypeError('Network error during upload'));
    xhr.onabort = () => reject(new DOMException('Upload aborted', 'AbortError'));
    if (opts.signal) {
      if (opts.signal.aborted) {
        reject(new DOMException('Upload aborted', 'AbortError'));
        return;
      }
      opts.signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }
    xhr.send(body);
  });
}

/** The ingress refuses one request above ~105 MB, so a drop goes up in groups of at most 90 MB (contract v0.4). */
export const INTAKE_GROUP_MAX_BYTES = 90 * 1024 * 1024;

/** Splits files, in order, into groups of at most `maxBytes`. A file larger than `maxBytes` goes alone. */
export function groupFiles(files: File[], maxBytes = INTAKE_GROUP_MAX_BYTES): File[][] {
  const groups: File[][] = [];
  let current: File[] = [];
  let size = 0;
  for (const f of files) {
    if (current.length > 0 && size + f.size > maxBytes) {
      groups.push(current);
      current = [];
      size = 0;
    }
    current.push(f);
    size += f.size;
  }
  if (current.length > 0) groups.push(current);
  return groups;
}

/** One POST: the first group creates the batch, a later group appends to it. */
async function postGroup(url: string, body: FormData, opts: UploadBatchOptions, onProgress: (f: number) => void) {
  const groupOpts = { ...opts, onProgress };
  let res = await send(url, body, await authHeaders({ multipart: true }), groupOpts);
  if (res.status === 401) {
    onProgress(0);
    res = await send(url, body, await authHeaders({ multipart: true, refresh: true }), groupOpts);
  }
  if (res.status < 200 || res.status >= 300) throw toIntakeError(res.status, res.text);
  try {
    return JSON.parse(res.text) as IntakeUploadResponse;
  } catch {
    throw new IntakeApiError(res.status, 'invalid_response', null);
  }
}

/**
 * Uploads one batch, in groups of at most 90 MB. The first group goes to `.../Intake/Batches` (with `source` and the
 * optional `context`) and creates the batch; each later group goes to `.../Batches/{batchId}/Files`. The replies are
 * joined into one response. If a later group fails, the files already sent stay in the batch, and the unsent files
 * come back as `Refused` with the client code `upload_failed`, so each line says what happened to it.
 */
export async function uploadBatch(
  patientId: string,
  files: File[],
  opts: UploadBatchOptions = {},
): Promise<IntakeUploadResponse> {
  // An empty list would create no batch and answer a null batchId: refuse it as a call error instead.
  if (files.length === 0) throw new IntakeApiError(0, 'no_files', null);
  // A file over the group limit would fail at the ingress and take its group down with it: refuse it here, per file.
  const oversize = files.filter((f) => f.size > INTAKE_GROUP_MAX_BYTES);
  const sendable = files.filter((f) => f.size <= INTAKE_GROUP_MAX_BYTES);
  const groups = groupFiles(sendable);
  const total = sendable.reduce((n, f) => n + f.size, 0) || 1;
  let sentBytes = 0;
  let batchId: string | null = null;
  const out: IntakeUploadResponse['files'] = [];

  for (let g = 0; g < groups.length; g++) {
    // A cancel between groups stops the upload; it is not a failed group.
    if (opts.signal?.aborted) throw new DOMException('Upload aborted', 'AbortError');
    const group = groups[g];
    const groupBytes = group.reduce((n, f) => n + f.size, 0);
    const body = new FormData();
    for (const f of group) body.append('files', f, f.name);
    let url: string;
    if (batchId === null) {
      body.append('source', opts.source ?? 'upload');
      if (opts.context) body.append('context', JSON.stringify(opts.context));
      url = apiUrl(`${intakeBase(patientId)}/Batches`);
    } else {
      url = apiUrl(`${intakeBase(patientId)}/Batches/${encodeURIComponent(batchId)}/Files`);
    }
    const progress = (f: number) => opts.onProgress?.((sentBytes + f * groupBytes) / total);
    try {
      const res = await postGroup(url, body, opts, progress);
      batchId = res.batchId;
      out.push(...res.files);
    } catch (e) {
      // Nothing was stored yet, or the user cancelled: the caller sees the error itself.
      if (batchId === null || opts.signal?.aborted) throw e;
      for (const f of groups.slice(g).flat()) {
        out.push({ fileName: f.name, documentId: null, runId: null, status: 'Refused', refusedCode: 'upload_failed' });
      }
      break;
    }
    sentBytes += groupBytes;
  }

  for (const f of oversize) {
    out.push({ fileName: f.name, documentId: null, runId: null, status: 'Refused', refusedCode: 'too_large' });
  }
  opts.onProgress?.(1);
  // No batch when every file was refused here: `batchId` stays null.
  return { batchId, files: out };
}
